import React, { Fragment, useEffect, useState } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FacebookIcon from "@mui/icons-material/Facebook";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchBar from "@components/inputs/SearchBar/SearchBar";
import InfoCard from "@components/display/InfoCard/InfoCard";
import PaginationControl from "@components/controls/PaginationControl/PaginationControl";
import Button from "@components/buttons/Button/Button";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { formatDate, formatTime } from "@util/string";
import { UpcomingService } from "@models/UpcomingService";
import { deleteUpcomingService, fetchUpcomingServices } from "@services/upcomingServiceService";
import { filterByFullName } from "@util/filter";
import DeleteConfirmationPopup from "@components/modals/DeleteConfirmationPopup/DeleteConfirmationPopup";
import { createMemorialFromService } from "@services/memorialService";
import { useNavigate } from "react-router-dom";
import { getRootDomain } from "@util/domain";
import { useAuth } from "@context/auth-context";
import Switch from "@components/controls/Switch/Switch";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { shareToFacebook } from "@util/share";
import Snackbar from "@components/display/SnackBar/SnackBar";
import AvailableWithPlatformAccessButton from "@components/buttons/AvailableWithPlatformAccessButton/AvailableWithPlatformAccessButton";

const RESULTS_PER_PAGE = 8;

const UpcomingServicesPage: React.FC = () => {
    const navigate = useNavigate();
    const { isSubscribed } = useAuth();
    const { authState, refreshUser } = useAuth();

    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [services, setServices] = useState<UpcomingService[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState("");
    const [showPreviousServices, setShowPreviousServices] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "info" | "warning";
    }>({
        open: false,
        message: "",
        severity: "success",
    });

    // Fetch services on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await fetchUpcomingServices();
                setServices(data);
            } catch (err) {
                setError("Failed to fetch services.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, []);

    const now = new Date().toISOString(); // Current time in UTC
    const twelveHoursAgo = new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(); // 12 hours ago in UTC

    const filteredData = filterByFullName(
        services.filter((upcomingService) => {
            // If showPreviousServices is true, show all services
            if (showPreviousServices) return true;

            // Otherwise, show services with either an empty services array
            // or at least one future sub-service or a sub-service from the last 12 hours
            return upcomingService.services.length === 0 // Include if services array is empty
                || upcomingService.services.some((subService) => {
                    const subServiceDateTime = subService.dateTime ? new Date(subService.dateTime).toISOString() : null;
                    return !subService.dateTime // Include if no dateTime
                        || (subServiceDateTime && (subServiceDateTime > now || subServiceDateTime >= twelveHoursAgo));
                });
        }),
        searchTerm,
    );

    // Paginate
    const totalPages = Math.ceil(filteredData.length / RESULTS_PER_PAGE);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * RESULTS_PER_PAGE,
        currentPage * RESULTS_PER_PAGE,
    );

    // Step back a page if deleting/filtering leaves the current page empty
    useEffect(() => {
        if (currentPage > 1 && currentPage > totalPages) {
            setCurrentPage(Math.max(1, totalPages));
        }
    }, [currentPage, totalPages]);

    const handleCreateMemorial = async (serviceId: string) => {
        if ((authState.user?.userType === "business_user")
            || (authState.user?.userType === "business_admin")) {
            // Additional memorials beyond the monthly allocation are billed automatically -
            // businesses are only blocked here if a prior overage charge failed.
            if (authState.user?.paymentLocked) {
                navigate("/account");
                return;
            }
        }
        try {
            setLoading(true);
            const memorialId = await createMemorialFromService(serviceId);
            void refreshUser();
            navigate(`/memorials/editor?${new URLSearchParams({ mode: "edit", id: memorialId }).toString()}`);
        } catch {
            setError("Failed to create memorial.");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        navigate("editor?mode=add");
    };

    const handleEdit = async (id: string) => {
        navigate(`editor?${new URLSearchParams({
            mode: "edit",
            id,
        }).toString()}`);
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            if (!selectedServiceId) {
                throw new Error("No service ID selected.");
            }
            await deleteUpcomingService(selectedServiceId);
            // Update charity status locally
            setServices((prevState) => (
                prevState.filter((val) => val.id !== selectedServiceId)
            ));
        } catch {
            setError("Failed to delete item.");
        } finally {
            setLoading(false);
        }
        setShowDeletePopup(false);
    };

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (paginatedData.length === 0) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>{`No funeral notices ${searchTerm ? "found" : "yet"}.`}</div>;
        }

        if (!isSubscribed) {
            return (
                <div className={commonStyles.container}>
                    <AvailableWithPlatformAccessButton />
                </div>
            );
        }

        return (
            <div className={commonStyles.itemList}>
                {paginatedData.map((data) => {
                    const url = `${getRootDomain(data.subdomain || "memorials")}/${data.memorialId ? `memorials/${data.memorialId}` : `services/${data.id}`}`
                    return (
                        <InfoCard
                            key={data.id}
                            title={`${data.firstName} ${data.lastName}`}
                            media={{
                                type: "image",
                                src: data.image || "/assets/images/placeholder-person.png",
                            }}
                            coverMedia
                            items={[
                                ...(data.birthDate && data.deathDate
                                    ? [`${formatDate(data.birthDate)} - ${formatDate(data.deathDate)}`]
                                    : data.birthYear && data.deathYear
                                        ? [`${data.birthYear} - ${data.deathYear}`]
                                        : []
                                ),
                                ...(data.directCremation
                                    ? ["Direct Cremation"]
                                    : data.privateService
                                        ? ["Private Service"]
                                        : data.services.length > 0
                                            ? data.services.map((service) => {
                                                const rsvpCount = (service.rsvps || []).reduce(
                                                    (total, rsvp) => total + (rsvp.attendees || 0),
                                                    0,
                                                );
                                                return `${service.type || "Service"}: ${
                                                    service.dateTime
                                                        ? `${formatDate(service.dateTime)}, ${formatTime(service.dateTime)}`
                                                        : "Not Set"
                                                }${data.enableRsvp ? ` (${rsvpCount} Attendees)` : ""}`;
                                            })
                                            : ["No Service Set"]
                                ),
                            ]}
                            buttons={[
                                // {
                                //     title: (
                                //         <>
                                //             Share
                                //             <ShareIcon
                                //                 style={{ marginLeft: "3px" }}
                                //                 fontSize="inherit"
                                //             />
                                //         </>
                                //     ),
                                //     variant: "text",
                                //     onClick: () => handleShare(
                                //         `${data.firstName} ${data.lastName} | Funeral Notice | Forever In Our Hearts`,
                                //         `Funeral notice for ${data.firstName} ${data.lastName}. View details and share your condolences.`,
                                //         url,
                                //     ),
                                // },
                                ...(!data.memorialId
                                    ? [
                                        {
                                            title: "Create Memorial +",
                                            variant: "submit" as const,
                                            onClick: () => handleCreateMemorial(data.id),
                                        },
                                    ]
                                    : []),
                                ...(data.enableRsvp
                                    ? [
                                        {
                                            title: "RSVPs",
                                            variant: "secondary" as const,
                                            onClick: () => navigate(`rsvps?${new URLSearchParams({ id: data.id }).toString()}`),
                                        },
                                    ]
                                    : []),
                                {
                                    title: (
                                        <>
                                            <FacebookIcon
                                                style={{ marginRight: "3px" }}
                                                fontSize="medium"
                                            />
                                            Share
                                        </>
                                    ),
                                    variant: "text",
                                    onClick: async () => shareToFacebook(url),
                                },
                                {
                                    title: (
                                        <>
                                            <ContentCopyIcon
                                                style={{ marginRight: "3px" }}
                                                fontSize="inherit"
                                            />
                                            Copy Link
                                        </>
                                    ),
                                    variant: "text",
                                    onClick: async () => {
                                        try {
                                            await navigator.clipboard.writeText(url);
                                            setSnackbar({
                                                open: true,
                                                message: "Link copied to clipboard",
                                                severity: "success",
                                            });
                                        } catch {
                                            setSnackbar({
                                                open: true,
                                                message: "Failed to copy link",
                                                severity: "error",
                                            });
                                        }
                                    },
                                },
                                {
                                    title: (
                                        <>
                                            View
                                            <OpenInNewIcon
                                                style={{ marginLeft: "3px" }}
                                                fontSize="inherit"
                                            />
                                        </>
                                    ),
                                    variant: "text",
                                    href: url,
                                    newTab: true,
                                },
                                {
                                    title: "Edit",
                                    variant: "secondary",
                                    onClick: () => handleEdit(data.id),
                                },
                                {
                                    title: <DeleteOutlineIcon fontSize="small" />,
                                    variant: "cancel",
                                    onClick: () => {
                                        setShowDeletePopup(true);
                                        setSelectedServiceId(data.id);
                                    },
                                },
                            ]}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div className={commonStyles.container}>
            <div className={commonStyles.headerContainer}>
                <h1>Funeral Notices</h1>
                {!loading && !error && isSubscribed && (
                    <div className={commonStyles.buttonGroup}>
                        <Button variant="primary" size="medium" onClick={handleAdd}>
                            Add
                        </Button>
                    </div>
                )}
            </div>
            {!loading && isSubscribed && (
                <>
                    <div className={commonStyles.searchBar}>
                        <SearchBar value={searchTerm} onChange={(val) => setSearchTerm(val)} />
                    </div>
                    <div className={commonStyles.switchContainer}>
                        <div>Show previous notices</div>
                        <Switch
                            value={showPreviousServices}
                            onChange={(val) => setShowPreviousServices(val)}
                        />
                    </div>
                </>
            )}
            {renderContent()}
            {totalPages > 1 && isSubscribed && (
                <div className={commonStyles.pagination}>
                    <PaginationControl
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                    />
                </div>
            )}
            <DeleteConfirmationPopup
                visible={showDeletePopup}
                onConfirm={handleDelete}
                onCancel={() => setShowDeletePopup(false)}
            />

            <Snackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </div>
    );
};

export default UpcomingServicesPage;
